// Client-side mirror of the backend billing engine (compute_session_charge in
// backend/app/services/billing.py, resolve_rate_at_instant in pricing.py,
// parse_time_to_minutes / minute_in_window in backend/app/utils/dates.py).
// The live bill on a table card must agree with the
// amount the backend charges on stop — keep these two in sync.

const BUSINESS_TZ = "Asia/Kolkata";

// Controller counts a PlayStation game can be started with. Keep in sync with
// CONTROLLER_COUNTS in backend/app/services/pricing.py.
export const CONTROLLER_COUNTS = ["1", "2", "3", "4"];

export const controllerLabel = (count) => `${count} Controller${String(count) === "1" ? "" : "s"}`;

/**
 * One console's rate for a controller count. A count with no rate falls back to
 * the 2-controller rate — what a solo player paid before 1 controller existed —
 * mirroring backend controller_rates / window_rate_for_table.
 */
export function controllerRate(rates, count) {
  const value = rates?.[String(count)];
  if (value !== undefined && value !== null && value !== "") return value;
  return rates?.["2"];
}

/** "HH:MM" -> minutes since midnight, or null when unparseable. */
export function parseTimeToMinutes(value) {
  if (typeof value !== "string") return null;
  const parts = value.trim().split(":");
  if (parts.length !== 2) return null;
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Half-open [start, end); end before start wraps past midnight. */
export function minuteInWindow(minuteOfDay, startMin, endMin) {
  if (startMin === endMin) return false;
  if (startMin < endMin) return minuteOfDay >= startMin && minuteOfDay < endMin;
  return minuteOfDay >= startMin || minuteOfDay < endMin;
}

/** Minute-of-day and weekday (Mon=0) for a timestamp, in the venue's timezone. */
export function venueMinuteAndDay(dateMs) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_TZ,
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hour12: false,
  }).formatToParts(new Date(dateMs));
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const hour = Number(get("hour")) % 24;
  const minute = Number(get("minute"));
  const weekdayIndex = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(get("weekday"));
  return { minuteOfDay: hour * 60 + minute, weekday: weekdayIndex };
}

/** { rate, label } in effect at a timestamp. Highest-priority window wins. */
export function resolveRateAtInstant(plan, dateMs) {
  const baseRate = Number(plan?.base_rate) || 0;
  const windows = Array.isArray(plan?.windows) ? plan.windows : [];
  if (windows.length === 0) return { rate: baseRate, label: "Normal" };

  const { minuteOfDay, weekday } = venueMinuteAndDay(dateMs);
  let best = null;
  for (const w of windows) {
    const days = Array.isArray(w.days) ? w.days : [];
    if (days.length > 0 && !days.includes(weekday)) continue;
    const startMin = parseTimeToMinutes(w.start);
    const endMin = parseTimeToMinutes(w.end);
    if (startMin === null || endMin === null) continue;
    if (!minuteInWindow(minuteOfDay, startMin, endMin)) continue;
    const rate = Number(w.rate);
    if (!(rate > 0)) continue;
    const priority = Number(w.priority) || 0;
    if (best === null || priority >= best.priority) {
      best = { priority, rate, label: w.label || "Offer" };
    }
  }
  return best ? { rate: best.rate, label: best.label } : { rate: baseRate, label: "Normal" };
}

/**
 * Wall-clock stretches actually in play: [start, end] with every pause cut out.
 * pauses: [[startMs, endMs], ...] — clamped to the session, may overlap.
 * Mirrors backend played_intervals.
 */
export function playedIntervals(startMs, endMs, pauses = []) {
  const cuts = pauses
    .map(([s, e]) => [Math.max(s, startMs), Math.min(e, endMs)])
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);

  const intervals = [];
  let cursor = startMs;
  for (const [s, e] of cuts) {
    if (s > cursor) intervals.push([cursor, s]);
    cursor = Math.max(cursor, e);
  }
  if (endMs > cursor) intervals.push([cursor, endMs]);
  return intervals;
}

/**
 * Charge completed whole minutes of play, each at the rate active on the wall
 * clock when it was played, then round the summed total up to the rupee —
 * identical to the backend. Paused time is not charged.
 * Returns { totalAmount, segments, billedMinutes, playedMs }.
 */
export function computeSessionCharge(plan, startMs, endMs, pauses = [], phases = []) {
  const intervals = playedIntervals(startMs, endMs, pauses);
  const playedMs = intervals.reduce((sum, [s, e]) => sum + (e - s), 0);
  const billedMinutes = Math.floor(playedMs / 60000);

  const ordered = [...phases].sort((a, b) => a[0] - b[0]);
  // The plan in force at an instant, plus that stretch's label — how a mid-game
  // controller swap splits the bill. No phases: the one plan, no suffix.
  const planAt = (instant) => {
    let active = plan;
    let suffix = "";
    for (const [at, phasePlan, phaseLabel] of ordered) {
      if (at > instant) break;
      active = phasePlan;
      suffix = phaseLabel;
    }
    return [active, suffix];
  };

  const buckets = new Map(); // "rate|label" -> { rate, label, minutes }
  // Walk the play clock: minute i begins i*60s into play.
  let idx = 0;
  let consumed = 0; // play ms in intervals before idx
  for (let i = 0; i < billedMinutes; i += 1) {
    const offset = i * 60000;
    while (idx < intervals.length - 1) {
      const length = intervals[idx][1] - intervals[idx][0];
      if (offset < consumed + length) break;
      consumed += length;
      idx += 1;
    }
    const instant = intervals[idx][0] + (offset - consumed);
    const [activePlan, phaseLabel] = planAt(instant);
    const { rate, label: rateLabel } = resolveRateAtInstant(activePlan, instant);
    const label = phaseLabel ? `${rateLabel} · ${phaseLabel}` : rateLabel;
    const key = `${rate}|${label}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.minutes += 1;
    else buckets.set(key, { rate, label, minutes: 1 });
  }

  const segments = [...buckets.values()].map((b) => ({
    ...b,
    amount: (b.minutes * b.rate) / 60,
  }));
  const totalAmount = segments.length
    ? Math.ceil(segments.reduce((sum, s) => sum + s.amount, 0))
    : 0;
  return { totalAmount, segments, billedMinutes, playedMs };
}

/**
 * A session's pauses as [startMs, endMs] pairs, with a pause still in progress
 * running until nowMs — so a paused game's clock and bill stay frozen.
 * Mirrors backend session_pauses.
 */
export function sessionPauses(session, nowMs) {
  const pauses = (Array.isArray(session?.pauses) ? session.pauses : [])
    .map((p) => [new Date(p.start).getTime(), new Date(p.end).getTime()])
    .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e));
  if (session?.paused_at) pauses.push([new Date(session.paused_at).getTime(), nowMs]);
  return pauses;
}

/** Pricing plan for a session: the snapshot if present, else a flat plan. */
export function planFromSession(session) {
  if (session?.pricing_plan?.base_rate != null) return session.pricing_plan;
  return { base_rate: Number(session?.price_per_hour) || 0, windows: [] };
}

/**
 * [atMs, plan, label] per stretch of a game whose price changed part-way through:
 * a different number of controllers, or a move to another table. [] when neither
 * happened, so an ordinary game bills exactly as before. Mirrors backend
 * session_plan_phases.
 */
export function sessionPlanPhases(session) {
  const changes = Array.isArray(session?.controller_changes) ? session.controller_changes : [];
  const moves = Array.isArray(session?.table_moves) ? session.table_moves : [];
  const swapped = changes.length >= 2;
  if (!swapped && moves.length === 0) return [];

  const usable = (plan) => plan?.base_rate != null;
  const plans = session?.pricing_plans || {};
  const opening = planFromSession(session);

  const events = [
    ...changes.map((c) => {
      // A change made before a move carries its own plan, frozen by the move.
      const plan = usable(c.plan) ? c.plan : plans[String(c.count)];
      return { at: new Date(c.at).getTime(), plan: usable(plan) ? plan : opening, count: c.count, onConsole: true };
    }),
    ...moves.map((m) => ({
      at: new Date(m.at).getTime(),
      plan: usable(m.plan) ? m.plan : opening,
      count: null,
      onConsole: m.to_table_type === "playstation",
    })),
  ].filter((e) => Number.isFinite(e.at)).sort((a, b) => a.at - b.at);

  let count = null;
  return events.map((e) => {
    // Pads mean nothing once the game has left the console.
    count = e.onConsole ? (e.count ?? count) : null;
    return [e.at, e.plan, swapped && count != null ? controllerLabel(count) : ""];
  });
}

/**
 * A game's whole bill, one stretch per table it was played on. Each table stands
 * alone, on its own minute clock and with its own whole-rupee total, so the next
 * table starts from nothing and the stretches add up by hand. A game that never
 * moved is a single stretch. Mirrors backend table_stretches / charge_to.
 * Returns { totalAmount, segments, billedMinutes, playedMs, stretches }.
 */
export function computeGameCharge(session, endMs, pauses = []) {
  const plan = planFromSession(session);
  const phases = sessionPlanPhases(session);
  const moves = (Array.isArray(session?.table_moves) ? session.table_moves : [])
    .map((m) => [new Date(m.at).getTime(), m])
    .filter(([at]) => Number.isFinite(at))
    .sort((a, b) => a[0] - b[0]);

  const names = [...moves.map(([, m]) => m.from_table_name), session?.table_name];
  const begins = [new Date(session?.start_time).getTime(), ...moves.map(([at]) => at)];
  const ends = [...moves.map(([at]) => at), endMs];
  const stretches = names.map((tableName, i) => {
    const charge = computeSessionCharge(plan, begins[i], ends[i], pauses, phases);
    return {
      tableName,
      from: begins[i],
      to: ends[i],
      amount: charge.totalAmount,
      minutes: charge.billedMinutes,
      playedMs: charge.playedMs,
      segments: charge.segments,
    };
  });

  const merged = new Map(); // "rate|label" -> segment
  stretches.flatMap((s) => s.segments).forEach((seg) => {
    const key = `${seg.rate}|${seg.label}`;
    const entry = merged.get(key) || { ...seg, minutes: 0, amount: 0 };
    entry.minutes += seg.minutes;
    entry.amount += seg.amount;
    merged.set(key, entry);
  });
  const sum = (field) => stretches.reduce((total, s) => total + s[field], 0);
  return {
    totalAmount: sum("amount"),
    segments: [...merged.values()],
    billedMinutes: sum("minutes"),
    playedMs: sum("playedMs"),
    stretches,
  };
}

/** Sorts a game's items into the stretch each was bought in. */
export function ordersByStretch(stretches, orders = []) {
  const begins = stretches.map((s) => new Date(s.from).getTime());
  const groups = stretches.map(() => []);
  orders.forEach((order) => {
    const at = new Date(order.created_at).getTime();
    let index = 0;
    while (index + 1 < begins.length && at >= begins[index + 1]) index += 1;
    groups[index].push(order);
  });
  return groups;
}

/** A tab grouped by item, oldest entry first within each line. */
export function groupTabLines(orders = []) {
  return Object.values(orders.reduce((acc, o) => {
    const line = acc[o.item_id] || (acc[o.item_id] = {
      itemId: o.item_id, name: o.item_name, quantity: 0, amount: 0, entries: [],
      players: new Set(),
    });
    line.quantity += o.quantity;
    line.amount += o.total_amount;
    if (o.player_name) line.players.add(o.player_name);
    line.entries.push(o); // oldest first, so the last one is the latest
    return acc;
  }, {}));
}
